import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { App } from "../../../routes/Constants";
import RemoveNurseModal from "./removeNurseModal";
import APIVariables from "../../../services/api-variables";
import Loader from "../../common/Loader/Loader";
import { handleCatch } from "../../../shared/helpers";
import { errorMessages } from "../../../constants/errorMessages.constants";
import Axios from "../../../services/Axios";
import DataTable from "@/global/tables/DataTable";
import { getNursesListColumns } from "./NursesListColumns";

const Nurses = () => {
  const [items, setItems] = useState([]);
  const [modalIsOpen, setIsModalOpen] = useState(false);
  const [selectedNurse, setSelectedNurse] = useState("");
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEntireNursesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEntireNursesList = async () => {
    setIsLoading(true);
    try {
      let acc = [];
      let nextUrl = APIVariables.HCP_CONNECTED_NURSES;

      while (nextUrl) {
        const res = await Axios.get(nextUrl);
        if (res.status !== 200) break;

        const { data } = res;

        if (Array.isArray(data)) {
          acc = data;
          nextUrl = null;
        } else {
          const chunk = data?.results ?? [];
          acc = acc.concat(chunk);
          nextUrl = data?.next || null;
        }
      }

      setItems(acc);
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const addNurse = () => {
    navigate(App.ADD_NURSE);
  };

  const openModalRemoveNurse = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedNurse(id);
    setIsModalOpen(true);
  }, []);

  const redirectToNurse = useCallback(
    (row) => {
      navigate(App.DEFAULT_EDIT_NURSE + `${row.id}`);
    },
    [navigate]
  );

  const closeModal = () => {
    setSelectedNurse("");
    setErrorMessage("");
    setIsModalOpen(false);
  };

  const removeNurse = async () => {
    setErrorMessage("");

    const res = await Axios.delete(
      APIVariables.HCP_CONNECTED_NURSES_DELETE + selectedNurse
    ).catch(function (error) {
      const errors = error.response.data;
      if (typeof errors === "string") {
        setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
      } else {
        const errorsArray = [];
        Object.keys(errors).forEach((key) => {
          errorsArray.push(key + " - " + errors[key]);
        });
        setErrorMessage(errorsArray);
      }
    });
    if (res?.status === 200) {
      const newItems = items.filter((item) => {
        return item.id !== selectedNurse;
      });
      setItems(newItems);
      closeModal();
    }
  };

  const columns = useMemo(
    () => getNursesListColumns({ onRemove: openModalRemoveNurse }),
    [openModalRemoveNurse]
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      className="min-h-screen overflow-y-auto pt-[140px] pb-[100px]"
      data-testid="nurses-page"
    >
      <div className="mx-auto max-w-[1100px] space-y-4">
        <div className="flex items-center justify-between py-2.5">
          <h1 className="h1 mineShaft-color" data-testid="nurses-heading">
            Connected Nurses
          </h1>
          <button
            type="button"
            className="second-button"
            onClick={addNurse}
            data-testid="nurses-add-button"
          >
            Add Nurse
          </button>
        </div>
        <div
          className="rounded-md border bg-white px-4 pb-4"
          data-testid="nurses-table"
        >
          <DataTable
            columns={columns}
            data={items}
            tableTitle="Connected nurses"
            onRowClick={redirectToNurse}
            loading={false}
            getRowTestId={(row) => `nurses-row-${row?.id}`}
          />
        </div>
        {modalIsOpen && (
          <RemoveNurseModal
            modalIsOpen={modalIsOpen}
            closeModal={closeModal}
            removeNurse={removeNurse}
            errorMessage={errorMessage}
          />
        )}
      </div>
    </div>
  );
};
export default Nurses;
